import {
  Mutation,
  PlaidAccountLinkTokenForRelinkV2Result,
  Query,
} from '@varo/graphql-schema-types';
import {
  isPlaidAccountLinkTokenForUpgradeError,
  isPlaidAccountLinkTokenForUpgradeSuccess,
  isPlaidAccountLinkTokenV2Error,
  isPlaidAccountLinkTokenV2Success,
  isPlaidRelinkTokenError,
  isPlaidRelinkTokenSuccess,
} from 'features/advance/helpers/plaid.helpers';
import { useCallback, useRef, useState } from 'react';
import { PlaidAccountLinkTokenForUpgrade } from 'services/graphql/mutations/Plaid/PlaidAccountLinkTokenForUpgrade';
import GetPlaidAccountLinkTokenV2 from 'services/graphql/mutations/Plaid/PlaidAccountLinkTokenV2';
import GetPlaidAccountRelinkToken from 'services/graphql/queries/GetPlaidAccountRelinkToken';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery, {
  NO_DATA_RETURNED,
} from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { CombinedError } from 'urql';

import { PlaidIntent, PlaidIntentTypes } from './types';

export type FetchTokenResult =
  | {
      status: 'success';
      token: string;
      linkReferenceId?: string;
    }
  | {
      status: 'failed';
    }
  | {
      status: 'aborted';
    }
  | {
      status: 'skipped';
      reason: 'already-fetching' | 'wait-intent';
    };

export type FetchToken = () => Promise<FetchTokenResult>;

type UseGetPlaidTokensResult = {
  token: string | null;
  linkReferenceId?: string;
  fetching: boolean;
  error: { title?: string; name: string; message: string } | null;
  fetchToken: FetchToken;
  reset: () => void;
};

type RelinkTokenQuery = {
  plaidAccountLinkTokenForRelinkV2?: PlaidAccountLinkTokenForRelinkV2Result | null;
};

const useGetPlaidTokens = (
  params: PlaidIntentTypes,
): UseGetPlaidTokensResult => {
  // Stable identity prevents restarting the token orchestration effect.
  // So here we are telling React compiler not to run useCallback optimizations on this hook.
  'use no memo';

  const [fetching, setFetching] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [linkReferenceId, setLinkReferenceId] = useState<string | undefined>(
    undefined,
  );
  const [error, setError] = useState<{ name: string; message: string } | null>(
    null,
  );

  // Keep a ref so fetchToken remains stable even if the intent object
  // reference changes between renders (e.g. inline object in the caller).
  // This prevents the auto-fetch useEffect in usePlaidV2 from re-firing
  // on every render due to fetchToken being recreated.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchingRef = useRef(false);

  const [, execLinkV2] = useCancellableMutation<Mutation>(
    GetPlaidAccountLinkTokenV2,
  );

  const [, execUpgrade] = useCancellableMutation<Mutation>(
    PlaidAccountLinkTokenForUpgrade,
  );

  const [execRelink] = useCancellableQuery<
    Pick<Query, 'plaidAccountLinkTokenForRelinkV2'>,
    { accountId: string }
  >(GetPlaidAccountRelinkToken);

  const fetchToken = useCallback(async (): Promise<FetchTokenResult> => {
    if (fetchingRef.current) {
      return {
        status: 'skipped',
        reason: 'already-fetching',
      };
    }
    fetchingRef.current = true;
    const params = paramsRef.current;
    setFetching(true);
    setError(null);

    try {
      switch (params?.intent) {
        case PlaidIntent.Link: {
          const res = await execLinkV2({
            input: { entrypoint: params.entryPoint },
          });

          const mutationError = isPlaidAccountLinkTokenV2Error(
            res.data?.plaidAccountLinkTokenV2,
          )
            ? res.data.plaidAccountLinkTokenV2
            : null;

          const error = res.error || mutationError;

          if (error) {
            logErrorWithContext(error, {
              featureArea: FeatureArea.PLAID,
              responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
              severity: ErrorSeverity.HIGH,
            });
            setError({
              message: error.message,
              name: mutationError
                ? 'PlaidAccountLinkTokenV2Error'
                : (res.error?.name ?? 'Error'),
            });
            return {
              status: 'failed',
            };
          }

          const success = isPlaidAccountLinkTokenV2Success(
            res.data?.plaidAccountLinkTokenV2,
          )
            ? res.data.plaidAccountLinkTokenV2
            : null;

          if (!success) {
            setError({
              name: 'UnexpectedResponseError',
              message: 'Unexpected response: missing link token data',
            });
            return {
              status: 'failed',
            };
          }

          const nextToken = success.linkToken;
          const nextLinkReferenceId = success.linkReferenceId ?? undefined;

          setToken(nextToken);
          setLinkReferenceId(nextLinkReferenceId);

          return {
            status: 'success',
            token: nextToken,
            linkReferenceId: nextLinkReferenceId,
          };
        }
        case PlaidIntent.CraUpgrade: {
          const res = await execUpgrade({
            input: { accountId: params.accountId },
          });

          const mutationError = isPlaidAccountLinkTokenForUpgradeError(
            res.data?.plaidAccountLinkTokenForUpgrade,
          )
            ? res.data.plaidAccountLinkTokenForUpgrade
            : null;

          const error = res.error || mutationError;

          if (error) {
            logErrorWithContext(error, {
              featureArea: FeatureArea.PLAID,
              responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
              severity: ErrorSeverity.HIGH,
            });

            setError({
              message: error.message,
              name: mutationError
                ? 'PlaidAccountLinkTokenForUpgradeError'
                : (res.error?.name ?? 'Error'),
            });
            return {
              status: 'failed',
            };
          }

          const success = isPlaidAccountLinkTokenForUpgradeSuccess(
            res.data?.plaidAccountLinkTokenForUpgrade,
          )
            ? res.data.plaidAccountLinkTokenForUpgrade
            : null;

          if (!success) {
            setError({
              name: 'UnexpectedResponseError',
              message: 'Unexpected response: missing upgrade token data',
            });
            return {
              status: 'failed',
            };
          }

          const nextToken = success.linkToken;

          setToken(nextToken);
          setLinkReferenceId(undefined);

          return {
            status: 'success',
            token: nextToken,
            linkReferenceId: undefined,
          };
        }
        case PlaidIntent.ReLink: {
          let res: RelinkTokenQuery;

          try {
            res = await execRelink(
              { accountId: params.accountId },
              { requestPolicy: 'network-only' },
            );
          } catch (relinkError) {
            if (!(relinkError instanceof CombinedError)) {
              throw relinkError;
            }

            if (relinkError.message.includes(NO_DATA_RETURNED)) {
              setError({
                name: 'UnexpectedResponseError',
                message: 'Unexpected response: missing relink token data',
              });
              return {
                status: 'failed',
              };
            }

            logErrorWithContext(relinkError, {
              featureArea: FeatureArea.PLAID,
              responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
              severity: ErrorSeverity.HIGH,
            });
            setError({
              message: relinkError.message,
              name: relinkError.name,
            });
            return {
              status: 'failed',
            };
          }

          const mutationError = isPlaidRelinkTokenError(
            res.plaidAccountLinkTokenForRelinkV2,
          )
            ? res.plaidAccountLinkTokenForRelinkV2
            : null;

          if (mutationError) {
            logErrorWithContext(mutationError, {
              featureArea: FeatureArea.PLAID,
              responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
              severity: ErrorSeverity.HIGH,
            });

            setError({
              message: mutationError.message,
              name: 'PlaidAccountLinkTokenForRelinkV2Error',
            });
            return {
              status: 'failed',
            };
          }

          const success = isPlaidRelinkTokenSuccess(
            res.plaidAccountLinkTokenForRelinkV2,
          )
            ? res.plaidAccountLinkTokenForRelinkV2
            : null;

          if (!success) {
            setError({
              name: 'UnexpectedResponseError',
              message: 'Unexpected response: missing relink token data',
            });
            return {
              status: 'failed',
            };
          }

          const nextToken = success.linkToken;

          setToken(nextToken);
          setLinkReferenceId(undefined);

          return {
            status: 'success',
            token: nextToken,
            linkReferenceId: undefined,
          };
        }
        default: {
          return {
            status: 'skipped',
            reason: 'wait-intent',
          };
        }
      }
    } catch (e) {
      // A cancelled request is not a user-visible error — ignore silently.
      if (e instanceof AbortError) {
        return {
          status: 'aborted',
        };
      }

      setToken(null);
      setLinkReferenceId(undefined);

      logErrorWithContext(e, {
        featureArea: FeatureArea.PLAID,
        responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
        severity: ErrorSeverity.HIGH,
      });

      setError({
        message: e instanceof Error ? e.message : String(e),
        name: e instanceof Error ? e.name : 'Error',
      });

      return {
        status: 'failed',
      };
    } finally {
      fetchingRef.current = false;
      setFetching(false);
    }
  }, [execLinkV2, execRelink, execUpgrade]);

  const reset = useCallback(() => {
    setToken(null);
    setLinkReferenceId(undefined);
    setError(null);
  }, []);

  return {
    token,
    linkReferenceId,
    fetching,
    error,
    fetchToken,
    reset,
  };
};

export default useGetPlaidTokens;
